import PropTypes from 'prop-types'
import { useCallback, useMemo } from 'react'
import { connect } from 'react-redux'
import Select from 'react-select'
import { Col } from 'reactstrap'
import { buildSkuSuffix } from '../../../../helpers'
import { useProductAvailableOptions } from '../../../../hooks/useProductAvailableOptions'
import dollar_price from '../../../general/DollarPrice'
import { configurableSelectStyles } from '../../configurableSelectStyles'
import { mapDispatchToProps } from '../../productPageFunctions'

/**
 * @typedef {Object} ConfigurableProductProps
 * @property {number} index - The index of the product
 * @property {Object} variant - The variant object
 * @property {Object} product - The product object
 * @property {Object} configSelection - The current selections
 * @property {function} updateConfigSelection - The callback that updates selections
 * @property {Object} currentLocation - The current location object
 *
 */

/**
 * ConfigurableProduct component
 *
 * @description - renders a single product as part of a bundle
 *
 * @param {ConfigurableProductProps} props - the component properties
 * @returns {JSX.Element} - the rendered component corresponding to each product as part of a bundle
 */
const ConfigurableProduct = ({
  index,
  variant,
  product,
  configSelection,
  updateConfigSelection,
  currentLocation,
  availableSkus
}) => {
  const productAvailableSkus = useMemo(() => ({ ...product, available_skus: availableSkus }), [product, availableSkus])
  const availableOptions = useProductAvailableOptions(
    productAvailableSkus,
    configSelection[product.bundle_group_product_id]?.selected_options
  )

  const handleChange = useCallback(
    selectedSelectOption => {
      const variant_id = variant.variant_id

      const selectedOption = selectedSelectOption.option
      selectedOption.idx = index
      // send the necessary data to redux
      updateConfigSelection({
        product_id: product.product_id,
        bundle_group_product_id: product.bundle_group_product_id,
        variant_id,
        option: selectedOption
      })
    },
    [variant, index, product, updateConfigSelection]
  )

  const suffix = option => {
    if (availableOptions.allAvailable) {
      return ''
    }
    const sortedVariants = product.nested_data.sort((a, b) => {
      return a.sort_order - b.sort_order
    })
    if (!availableOptions[variant.variant_id]?.has(option.sku_code)) {
      const optimisticSelection = {
        ...(configSelection[product.bundle_group_product_id]?.selected_options ?? {}),
        [variant.configurable_variant_id]: option
      }
      const prospectiveSku = `${product.product_sku}${buildSkuSuffix(sortedVariants, optimisticSelection)}`
      const skuExists = productAvailableSkus.available_skus.find(sku => sku === prospectiveSku)

      if (!skuExists) {
        return ' - Unavailable'
      }
      return ' - Sold out'
    }
  }

  const selectionsForThisProduct = configSelection[product.bundle_group_product_id]
    ? configSelection[product.bundle_group_product_id].selected_options
    : {}

  const selectOptions = (variant.nested_data || []).map(option => ({
    value: option.id,
    label: option.name,
    option
  }))
  const selectedSelectOption = selectionsForThisProduct[variant.variant_id]
    ? selectOptions.find(selectOption => selectOption.value === selectionsForThisProduct[variant.variant_id].id)
    : null

  const formatSelectOptionLabel = selectOption => {
    const option = selectOption.option
    let optionInputLabel = option.name
    if (option.has_price_modifier === true && currentLocation.show_price) {
      const additionalPriceCents = option.price_modifier.price_modifier_cents

      optionInputLabel = (
        <>
          {optionInputLabel} + {dollar_price(additionalPriceCents)}
        </>
      )
    }

    return (
      <>
        {optionInputLabel}
        {suffix(option)}
      </>
    )
  }

  return (
    <Col className="mf-mt-1 configurable-select-option" xs="12" md="6">
      <b>{variant.display_name ? variant.display_name : variant.name}</b>
      <Select
        className="mf-mt-1 mf-mb-0 configurable-option-select"
        classNamePrefix="configurable-option-select"
        isClearable={false}
        options={selectOptions}
        value={selectedSelectOption || null}
        placeholder="Please select..."
        onChange={handleChange}
        formatOptionLabel={formatSelectOptionLabel}
        styles={configurableSelectStyles}
      />
    </Col>
  )
}

ConfigurableProduct.propTypes = {
  index: PropTypes.number.isRequired,
  product: PropTypes.object.isRequired,
  variant: PropTypes.object.isRequired,
  updateConfigSelection: PropTypes.func.isRequired,
  configSelection: PropTypes.object.isRequired,
  currentLocation: PropTypes.object.isRequired,
  availableSkus: PropTypes.array
}

const mapStateToProps = (state, { product }) => {
  return {
    configSelection: state.productPage.configurableSelection,
    currentLocation: state.storeViews.current,
    availableSkus: state.productPage.availableSkus[product.product_id]
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ConfigurableProduct)
